import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { purchaseReportingApi } from '../../services/purchaseReportingApi';
import type { PRFilters, PROverviewData } from '../../types/purchaseReporting';

interface Props {
    filters: PRFilters;
    selectedYear: number;
    refreshKey: number;
}

function formatNumber(n: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

function formatPct(current: number, previous: number): { delta: number; pct: string; positive: boolean } {
    const delta = current - previous;
    const pct = previous !== 0 ? ((delta / previous) * 100).toFixed(1) : current > 0 ? '100.0' : '0.0';
    return { delta, pct, positive: delta >= 0 };
}

export function OverviewTab({ filters, selectedYear, refreshKey }: Props) {
    const [data, setData] = useState<PROverviewData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);

        const maxMonth = selectedYear < new Date().getFullYear()
            ? 12
            : new Date().getMonth(); // 0-based = last complete month (1-based)

        purchaseReportingApi.getOverview({
            year: selectedYear,
            maxMonth,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            company: filters.company || undefined,
            businessUnit: filters.businessUnit || undefined,
            vendorID: filters.vendorID || undefined,
            locationID: filters.locationID || undefined,
        })
            .then(setData)
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [filters, selectedYear, refreshKey]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    if (error) {
        return <div className="text-red-600 p-4">{error}</div>;
    }

    if (!data) return null;

    const currentReceived = data.receivedByYear.find(r => r.year === selectedYear);
    const prevReceived = data.receivedByYear.find(r => r.year === selectedYear - 1);
    const prev2Received = data.receivedByYear.find(r => r.year === selectedYear - 2);
    const currentOrdered = data.orderedByYear.find(r => r.year === selectedYear);
    const prevOrdered = data.orderedByYear.find(r => r.year === selectedYear - 1);

    const receivedDelta = formatPct(currentReceived?.qtyReceived || 0, prevReceived?.qtyReceived || 0);
    const orderedDelta = formatPct(currentOrdered?.qtyOrdered || 0, prevOrdered?.qtyOrdered || 0);
    const amountDelta = formatPct(currentOrdered?.amountEUR || 0, prevOrdered?.amountEUR || 0);

    // Merge trend data for chart
    const trendMap = new Map<string, { period: string; current: number; previous: number }>();
    for (const t of data.trend) {
        const monthKey = t.period.slice(5); // MM
        trendMap.set(monthKey, { period: monthKey, current: t.qtyReceived, previous: 0 });
    }
    for (const t of data.trendPreviousYear) {
        const monthKey = t.period.slice(5);
        const existing = trendMap.get(monthKey);
        if (existing) {
            existing.previous = t.qtyReceived;
        } else {
            trendMap.set(monthKey, { period: monthKey, current: 0, previous: t.qtyReceived });
        }
    }
    const chartData = Array.from(trendMap.values()).sort((a, b) => a.period.localeCompare(b.period));

    return (
        <div className="space-y-6">
            {/* KPI Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Qty Received */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Qty Received (YTD)</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(currentReceived?.qtyReceived || 0)}</div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                        <span className={receivedDelta.positive ? 'text-green-600' : 'text-red-600'}>
                            {receivedDelta.positive ? '+' : ''}{receivedDelta.pct}%
                        </span>
                        <span className="text-gray-400">vs {selectedYear - 1}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-400">
                        {selectedYear - 1}: {formatNumber(prevReceived?.qtyReceived || 0)} | {selectedYear - 2}: {formatNumber(prev2Received?.qtyReceived || 0)}
                    </div>
                </div>

                {/* Qty Ordered */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Qty Ordered (YTD)</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(currentOrdered?.qtyOrdered || 0)}</div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                        <span className={orderedDelta.positive ? 'text-green-600' : 'text-red-600'}>
                            {orderedDelta.positive ? '+' : ''}{orderedDelta.pct}%
                        </span>
                        <span className="text-gray-400">vs {selectedYear - 1}</span>
                    </div>
                </div>

                {/* Amount EUR */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Amount EUR (YTD)</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(currentOrdered?.amountEUR || 0)} EUR</div>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                        <span className={amountDelta.positive ? 'text-green-600' : 'text-red-600'}>
                            {amountDelta.positive ? '+' : ''}{amountDelta.pct}%
                        </span>
                        <span className="text-gray-400">vs {selectedYear - 1}</span>
                    </div>
                </div>

                {/* Outstanding */}
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 uppercase">Qty Outstanding</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900">{formatNumber(data.qtyOutstanding)}</div>
                    <div className="mt-1 text-xs text-gray-400">EOS companies only</div>
                </div>
            </div>

            {/* Trend Chart */}
            <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Monthly Received Quantity</h3>
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} barGap={2}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} tickFormatter={v => formatNumber(v)} />
                            <Tooltip formatter={(value) => formatNumber(Number(value))} />
                            <Legend />
                            <Bar dataKey="current" name={String(selectedYear)} fill="#3b82f6" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="previous" name={String(selectedYear - 1)} fill="#d1d5db" radius={[2, 2, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Receipts count */}
            <div className="text-xs text-gray-400">
                {formatNumber(currentReceived?.receipts || 0)} receipt transactions in {selectedYear} YTD
            </div>
        </div>
    );
}
